import { z } from 'zod';
import { isCalendarDate, normalizeName } from './people.js';

export const seasonStatuses = ['draft', 'active', 'closed'] as const;
export type SeasonStatus = (typeof seasonStatuses)[number];

export const teamStatuses = ['draft', 'active', 'archived'] as const;
export type TeamStatus = (typeof teamStatuses)[number];

export const trainingGroupStatuses = ['active', 'archived'] as const;
export type TrainingGroupStatus = (typeof trainingGroupStatuses)[number];

const labelSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(100));
const nameSchema = z.string().transform(normalizeName).pipe(z.string().min(1).max(200));
const calendarDateSchema = z.string().refine(isCalendarDate);

/** Age-category codes are reference data (`U11`, `U7_U9`); the labels are translated in the catalogs. */
const categoryCodeSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_]{0,31}$/);

/** A new season starts as a draft; the server owns its status and version. */
export const createSeasonRequestSchema = z
  .strictObject({
    departmentId: z.uuid(),
    label: labelSchema,
    startsOn: calendarDateSchema,
    endsOn: calendarDateSchema,
  })
  .refine((request) => request.endsOn >= request.startsOn);
export type CreateSeasonRequest = z.infer<typeof createSeasonRequestSchema>;

export const createTeamRequestSchema = z.strictObject({
  seasonId: z.uuid(),
  categoryCode: categoryCodeSchema,
  name: nameSchema,
  competitionDivision: z
    .string()
    .transform(normalizeName)
    .pipe(z.string().max(200))
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .default(null),
  teamOrdinal: z.number().int().min(1).max(20).default(1),
});
export type CreateTeamRequest = z.infer<typeof createTeamRequestSchema>;

export const createTrainingGroupRequestSchema = z.strictObject({
  seasonId: z.uuid(),
  name: nameSchema,
});
export type CreateTrainingGroupRequest = z.infer<typeof createTrainingGroupRequestSchema>;

/** Starts a placement; the interval is inclusive and the start defaults to the club's current day. */
export const addTeamMemberRequestSchema = z
  .strictObject({
    athleteId: z.uuid(),
    fromDate: calendarDateSchema.optional(),
    untilDate: calendarDateSchema.nullable().default(null),
    shirtNumber: z.number().int().min(0).max(99).nullable().default(null),
  })
  .refine(
    (request) =>
      request.fromDate === undefined ||
      request.untilDate === null ||
      request.untilDate >= request.fromDate,
  );
export type AddTeamMemberRequest = z.infer<typeof addTeamMemberRequestSchema>;

export const addGroupMemberRequestSchema = z
  .strictObject({
    athleteId: z.uuid(),
    fromDate: calendarDateSchema.optional(),
    untilDate: calendarDateSchema.nullable().default(null),
  })
  .refine(
    (request) =>
      request.fromDate === undefined ||
      request.untilDate === null ||
      request.untilDate >= request.fromDate,
  );
export type AddGroupMemberRequest = z.infer<typeof addGroupMemberRequestSchema>;

/** Ends an open placement on the given day (default: the club's current day); history is kept. */
export const endPlacementRequestSchema = z.strictObject({
  untilDate: calendarDateSchema.optional(),
});
export type EndPlacementRequest = z.infer<typeof endPlacementRequestSchema>;

/**
 * Creates the next season as a draft with copies of the chosen teams and groups. Everything in the
 * source season stays as it was; with `carryOverMembers` the athletes active today are also placed
 * on the new teams and groups from the new season's start.
 */
export const rolloverRequestSchema = z
  .strictObject({
    label: labelSchema,
    startsOn: calendarDateSchema,
    endsOn: calendarDateSchema,
    carryOverMembers: z.boolean().default(false),
    teams: z
      .array(
        z.strictObject({
          sourceTeamId: z.uuid(),
          name: nameSchema.optional(),
          categoryCode: categoryCodeSchema.optional(),
        }),
      )
      .max(200),
    groups: z
      .array(z.strictObject({ sourceGroupId: z.uuid(), name: nameSchema.optional() }))
      .max(200),
  })
  .refine((request) => request.endsOn >= request.startsOn);
export type RolloverRequest = z.infer<typeof rolloverRequestSchema>;

export interface RolloverTeamProposal {
  sourceTeamId: string;
  name: string;
  categoryCode: string;
  competitionDivision: string | null;
  teamOrdinal: number;
  /** Active athletes who would be placed when members are carried over. */
  activeMembers: number;
}

export interface RolloverGroupProposal {
  sourceGroupId: string;
  name: string;
  activeMembers: number;
}

/** What a rollover would create. Nothing exists yet when the caller sees this. */
export interface RolloverPreviewView {
  sourceSeason: SeasonView;
  teams: RolloverTeamProposal[];
  groups: RolloverGroupProposal[];
}

export interface RolloverResultView {
  season: SeasonView;
  teamsCreated: number;
  groupsCreated: number;
  placementsCreated: number;
}

export interface DepartmentView {
  id: string;
  name: string;
  sportCode: string;
  /** Age-category codes enabled for the department's sport, in display order. */
  categoryCodes: string[];
  /** Whether the caller may create seasons, teams and groups in this department. */
  canManage: boolean;
}

export interface SeasonView {
  id: string;
  departmentId: string;
  label: string;
  /** Calendar days, `YYYY-MM-DD`. */
  startsOn: string;
  endsOn: string;
  status: SeasonStatus;
  version: number;
}

export interface TeamView {
  id: string;
  departmentId: string;
  seasonId: string;
  categoryCode: string;
  name: string;
  competitionDivision: string | null;
  teamOrdinal: number;
  status: TeamStatus;
  version: number;
}

export interface TrainingGroupView {
  id: string;
  departmentId: string;
  seasonId: string;
  name: string;
  status: TrainingGroupStatus;
  version: number;
}

/** One placement on a roster; it carries the athlete's name but no birth date or contact details. */
export interface RosterEntryView {
  id: string;
  athleteId: string;
  givenName: string;
  familyName: string;
  fromDate: string;
  untilDate: string | null;
  /** Team rosters only. */
  shirtNumber: number | null;
  /** Row version; send it in `If-Match` when ending the placement. */
  version: number;
}
